import dayjs from 'dayjs';
import { isNil } from 'lodash';

import { SHORT_DATETIME_FORMAT } from 'constants/date';

import { SHOP_PIPELINE_AS_OF_FALLBACK, SHOP_PIPELINE_AS_OF_PREFIX } from './shopPipeline.constants';

// Normalizes one backend loan row into the table display shape. `applicationId`
// is the stable list key + deep-link target; numeric counters default to 0 so a
// partial row never renders NaN. The lock-expiry stays raw ISO (null when absent)
// for the cell formatter to handle.
const mapLoan = (loan = {}) => ({
  applicationId: loan.applicationId ?? null,
  loanNumberLast4: loan.loanNumberLast4 ?? null,
  borrowerLastName: loan.borrowerLastName ?? null,
  loanOfficerName: loan.loanOfficerName ?? null,
  milestoneLabel: loan.milestoneLabel ?? null,
  daysInCurrentStatus: loan.daysInCurrentStatus ?? 0,
  openConditionCount: loan.openConditionCount ?? 0,
  lockExpirationDate: loan.lockExpirationDate ?? null,
});

const mapLoans = (loans = []) => loans.filter(loan => !isNil(loan)).map(mapLoan);

// Composes the freshness caption from the backend's real ISO `asOf` timestamp.
// Falls back to a neutral label when the backend omits it — never a fabricated
// timestamp.
export const getShopPipelineAsOfLabel = asOf => {
  if (isNil(asOf)) {
    return SHOP_PIPELINE_AS_OF_FALLBACK;
  }

  return `${SHOP_PIPELINE_AS_OF_PREFIX} ${dayjs(asOf).format(SHORT_DATETIME_FORMAT)}`;
};

/**
 * Maps the brokerPipeline/loans endpoint response into the homepage display
 * shape. Returns null when the response is missing so the reducer holds its empty
 * state.
 * @param {object|null} data - backend response ({ loans, asOf })
 * @returns {object|null} display model, or null when not renderable
 */
export const mapShopPipeline = data => {
  if (isNil(data)) {
    return null;
  }

  const { loans, asOf } = data;

  return {
    loans: mapLoans(loans),
    asOf: asOf ?? null,
    asOfLabel: getShopPipelineAsOfLabel(asOf),
  };
};

// Builds the distinct loan-officer option list (for the AutocompleteCDS filter)
// from the loaded rows, deduped + alphabetized. Each option carries the LO name
// as both label and stable key.
export const getDistinctLoanOfficerOptions = (loans = []) => {
  const names = loans.map(loan => loan.loanOfficerName).filter(name => !isNil(name) && name !== '');

  return [...new Set(names)].sort().map(name => ({ name, value: name }));
};

// Builds the distinct milestone option list from the loaded rows, deduped +
// alphabetized.
export const getDistinctMilestoneOptions = (loans = []) => {
  const milestones = loans
    .map(loan => loan.milestoneLabel)
    .filter(milestone => !isNil(milestone) && milestone !== '');

  return [...new Set(milestones)].sort().map(milestone => ({
    name: milestone,
    value: milestone,
  }));
};

// Applies the active loan-officer + milestone filters to the loaded rows. A null
// filter value means "all", so it is a no-op for that dimension.
export const getFilteredLoans = ({ loans = [], loanOfficerName = null, milestoneLabel = null }) =>
  loans
    .filter(loan => isNil(loanOfficerName) || loan.loanOfficerName === loanOfficerName)
    .filter(loan => isNil(milestoneLabel) || loan.milestoneLabel === milestoneLabel);

// Urgency-first sort for the dashboard preview. Priority:
//   1. Lock expiry soonest (ISO string compares lexicographically; null sorted last).
//   2. Most open conditions (highest first).
//   3. Most recently submitted / shortest days in current status (lowest first —
//      avoids surfacing stale test loans that have been sitting for months).
const compareLockExpiry = (a, b) => {
  if (isNil(a.lockExpirationDate) && isNil(b.lockExpirationDate)) {
    return 0;
  }

  if (isNil(a.lockExpirationDate)) {
    return 1;
  }

  if (isNil(b.lockExpirationDate)) {
    return -1;
  }

  return a.lockExpirationDate < b.lockExpirationDate ? -1 : 1;
};

/**
 * Sorts loans for the dashboard preview card by urgency:
 * lock-expiry soonest → most open conditions → fewest days in current status.
 * @param {Array} loans
 * @returns {Array}
 */
export const sortLoansByDashboardUrgency = (loans = []) =>
  [...loans].sort((a, b) => {
    const lockDiff = compareLockExpiry(a, b);

    if (lockDiff !== 0) {
      return lockDiff;
    }

    const conditionDiff = b.openConditionCount - a.openConditionCount;

    if (conditionDiff !== 0) {
      return conditionDiff;
    }

    return a.daysInCurrentStatus - b.daysInCurrentStatus;
  });
